/**
 * Animation Components & Utilities
 * Comprehensive animation system for CAF websites
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation, AnimatePresence, useScroll, useTransform } from 'framer-motion';

// ============================================
// SCROLL REVEAL ANIMATIONS
// ============================================

/**
 * Fade up animation when element comes into view
 */
export const FadeIn = ({ 
  children, 
  delay = 0, 
  duration = 0.6, 
  direction = 'up', // 'up', 'down', 'left', 'right', 'none'
  distance = 30,
  className = '',
  once = true,
  threshold = 0.1
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: threshold });
  
  const directions = {
    up: { y: distance, x: 0 },
    down: { y: -distance, x: 0 },
    left: { x: distance, y: 0 },
    right: { x: -distance, y: 0 },
    none: { x: 0, y: 0 }
  };
  
  const initial = { opacity: 0, ...directions[direction] };
  
  return (
    <motion.div
      ref={ref}
      initial={initial}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : initial}
      transition={{ duration, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Scale up animation
 */
export const ScaleIn = ({ 
  children, 
  delay = 0, 
  duration = 0.5,
  className = '',
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: 0.3 });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
      transition={{ duration, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Staggered children animation
 */
export const StaggerContainer = ({ 
  children, 
  staggerDelay = 0.1,
  className = '',
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: 0.2 });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        visible: {
          transition: {
            staggerChildren: staggerDelay
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = '' }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } }
    }}
    className={className}
  >
    {children}
  </motion.div>
);

// ============================================
// COUNTER ANIMATION
// ============================================

/**
 * Animated counter that counts up when in view
 */
export const AnimatedCounter = ({ 
  value, 
  duration = 2, 
  prefix = '', 
  suffix = '',
  className = '' 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isInView) return;
    
    const numericValue = parseInt(value.toString().replace(/\D/g, ''), 10);
    const startTime = Date.now();
    const endTime = startTime + duration * 1000;
    
    const updateCount = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / (duration * 1000), 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * numericValue));
      
      if (now < endTime) {
        requestAnimationFrame(updateCount);
      } else {
        setCount(numericValue);
      }
    };
    
    requestAnimationFrame(updateCount);
  }, [isInView, value, duration]);
  
  return (
    <span ref={ref} className={className}>
      {prefix}{count}{suffix}
    </span>
  );
};

// ============================================
// HOVER ANIMATIONS
// ============================================

/**
 * Card with lift effect on hover
 */
export const HoverCard = ({ 
  children, 
  className = '',
  liftAmount = -8,
  shadowColor = 'rgba(0,0,0,0.15)',
  ...props 
}) => (
  <motion.div
    className={className}
    whileHover={{ 
      y: liftAmount,
      boxShadow: `0 20px 40px ${shadowColor}`,
      transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }
    }}
    whileTap={{ scale: 0.98 }}
    {...props}
  >
    {children}
  </motion.div>
);

/**
 * Image with zoom effect on hover
 */
export const HoverZoomImage = ({ 
  src, 
  alt, 
  className = '',
  zoomScale = 1.1,
  duration = 0.5 
}) => (
  <motion.div className={`overflow-hidden ${className}`}>
    <motion.img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      whileHover={{ scale: zoomScale }}
      transition={{ duration, ease: [0.25, 0.1, 0.25, 1] }}
    />
  </motion.div>
);

/**
 * Button with micro-interactions
 */
export const AnimatedButton = ({ 
  children, 
  className = '',
  variant = 'scale', // 'scale', 'glow', 'bounce'
  onClick,
  ...props 
}) => {
  const variants = {
    scale: {
      whileHover: { scale: 1.05 },
      whileTap: { scale: 0.95 }
    },
    glow: {
      whileHover: { 
        scale: 1.02,
        boxShadow: '0 0 20px rgba(69, 108, 172, 0.5)'
      },
      whileTap: { scale: 0.98 }
    },
    bounce: {
      whileHover: { y: -3 },
      whileTap: { y: 0, scale: 0.95 }
    }
  };
  
  return (
    <motion.button
      className={className}
      onClick={onClick}
      {...variants[variant]}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      {...props}
    >
      {children}
    </motion.button>
  );
};

// ============================================
// HERO ANIMATIONS
// ============================================

/**
 * Floating element animation
 */
export const FloatingElement = ({ 
  children, 
  duration = 4, 
  distance = 15,
  delay = 0,
  className = '' 
}) => (
  <motion.div
    className={className}
    animate={{
      y: [-distance, distance, -distance],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    {children}
  </motion.div>
);

/**
 * Pulsing element
 */
export const PulseElement = ({ 
  children, 
  duration = 2,
  scale = 1.05,
  className = '' 
}) => (
  <motion.div
    className={className}
    animate={{
      scale: [1, scale, 1],
      opacity: [0.7, 1, 0.7]
    }}
    transition={{
      duration,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    {children}
  </motion.div>
);

/**
 * Rotating element
 */
export const RotatingElement = ({ 
  children, 
  duration = 20,
  className = '' 
}) => (
  <motion.div
    className={className}
    animate={{ rotate: 360 }}
    transition={{
      duration,
      repeat: Infinity,
      ease: "linear"
    }}
  >
    {children}
  </motion.div>
);

/**
 * Typewriter text effect
 */
export const TypewriterText = ({ 
  text, 
  speed = 50, 
  delay = 0,
  className = '' 
}) => {
  const [displayText, setDisplayText] = useState('');
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (!isInView || started) return;
    
    const timeout = setTimeout(() => {
      setStarted(true);
      let i = 0;
      const interval = setInterval(() => {
        setDisplayText(text.slice(0, i + 1));
        i++;
        if (i >= text.length) clearInterval(interval);
      }, speed);
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timeout);
  }, [isInView, text, speed, delay, started]);
  
  return (
    <span ref={ref} className={className}>
      {displayText}
      <motion.span
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.5, repeat: Infinity }}
      >
        |
      </motion.span>
    </span>
  );
};

// ============================================
// PARALLAX EFFECTS
// ============================================

/**
 * Parallax container - moves slower than scroll
 */
export const ParallaxSection = ({ 
  children, 
  speed = 0.5, // 0 = fixed, 1 = normal scroll
  className = '' 
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [0, 200 * (1 - speed)]);
  
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.div style={{ y }}>
        {children}
      </motion.div>
    </div>
  );
};

/**
 * Parallax background image
 */
export const ParallaxBackground = ({ 
  src, 
  speed = 0.3,
  className = '' 
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [-100, 100]);
  
  return (
    <div ref={ref} className={`absolute inset-0 overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt=""
        className="w-full h-[120%] object-cover"
        style={{ y }}
      />
    </div>
  );
};

// ============================================
// PAGE TRANSITIONS
// ============================================

/**
 * Page transition wrapper
 */
export const PageTransition = ({ children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * Slide page transition
 */
export const SlidePageTransition = ({ children, direction = 'left', className = '' }) => {
  const directions = {
    left: { enter: { x: 100 }, exit: { x: -100 } },
    right: { enter: { x: -100 }, exit: { x: 100 } },
    up: { enter: { y: 100 }, exit: { y: -100 } },
    down: { enter: { y: -100 }, exit: { y: 100 } }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, ...directions[direction].enter }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      exit={{ opacity: 0, ...directions[direction].exit }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// LOADING ANIMATIONS
// ============================================

/**
 * Skeleton loader with pulse animation
 */
export const Skeleton = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  className = '' 
}) => (
  <motion.div
    className={className}
    style={{ width, height, borderRadius, backgroundColor: '#e5e7eb' }}
    animate={{ opacity: [0.5, 1, 0.5] }}
    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
  />
);

/**
 * Spinner loading animation
 */
export const Spinner = ({ size = 24, color = '#456CAC', className = '' }) => (
  <motion.div
    className={className}
    style={{
      width: size,
      height: size,
      border: `3px solid ${color}20`,
      borderTop: `3px solid ${color}`,
      borderRadius: '50%'
    }}
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
  />
);

/**
 * Dots loading animation
 */
export const LoadingDots = ({ color = '#456CAC', size = 8, className = '' }) => (
  <div className={`flex gap-1 ${className}`}>
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: color
        }}
        animate={{ y: [0, -10, 0] }}
        transition={{
          duration: 0.6,
          delay: i * 0.15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    ))}
  </div>
);

// ============================================
// SPECIAL EFFECTS
// ============================================

/**
 * Gradient shift background
 */
export const GradientShift = ({ 
  colors = ['#456CAC', '#3A5A91', '#456CAC'],
  duration = 5,
  className = '',
  children 
}) => (
  <motion.div
    className={className}
    animate={{
      background: colors.map(c => `linear-gradient(135deg, ${c} 0%, ${colors[(colors.indexOf(c) + 1) % colors.length]} 100%)`)
    }}
    transition={{
      duration,
      repeat: Infinity,
      ease: "linear"
    }}
  >
    {children}
  </motion.div>
);

/**
 * Reveal mask effect
 */
export const RevealMask = ({ 
  children, 
  direction = 'left', // 'left', 'right', 'top', 'bottom'
  duration = 0.8,
  delay = 0,
  className = '' 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
  const clipPaths = {
    left: { hidden: 'inset(0 100% 0 0)', visible: 'inset(0 0% 0 0)' },
    right: { hidden: 'inset(0 0 0 100%)', visible: 'inset(0 0 0 0%)' },
    top: { hidden: 'inset(100% 0 0 0)', visible: 'inset(0% 0 0 0)' },
    bottom: { hidden: 'inset(0 0 100% 0)', visible: 'inset(0 0 0% 0)' }
  };
  
  return (
    <motion.div
      ref={ref}
      initial={{ clipPath: clipPaths[direction].hidden }}
      animate={isInView ? { clipPath: clipPaths[direction].visible } : {}}
      transition={{ duration, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Text highlight effect
 */
export const HighlightText = ({ 
  children, 
  color = '#456CAC',
  delay = 0,
  className = '' 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  
  return (
    <span ref={ref} className={`relative inline-block ${className}`}>
      <motion.span
        className="absolute inset-0 -z-10"
        style={{ backgroundColor: `${color}30` }}
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
        transformOrigin="left"
      />
      {children}
    </span>
  );
};

// Export motion for custom usage
export { motion, AnimatePresence, useInView, useAnimation, useScroll, useTransform };
